import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, User, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { productImg } from "@/lib/img";
import { CheatSheetForm } from "@/components/CheatSheetForm";
import { ProductFaq } from "@/components/product/ProductSections";
import { ShopBuyModal } from "@/components/ShopBuyModal";

const WHO = [
  [Users, "Parents", "Know what's happening on the field and what your kid's coach means."],
  [User, "Players", "Learn the keys, reads and words your coach expects you to know."],
  [ClipboardList, "New coaches", "Got voluntold? Start here: every word explained the first time it shows up."],
];
const FAQ = [
  ["What age is this for?", "Players about 10 to 16, and the adults around them. The Beginner's Guide starts from zero."],
  ["Is it the same as the Supercell books?", "No. These teach general football, not one team's system. The Supercell books are for coaches running that offense."],
  ["What do I get?", "PDFs you download right away and can print."],
];
const price = (p) => (p.free ? "Free" : `$${p.amount / 100}`);
const H2 = ({ children, id }) => <h2 id={id} className="font-head font-semibold uppercase text-3xl sm:text-4xl text-[#111315] leading-[1.02]">{children}</h2>;

function BookCard({ p, onBuy, badge }) {
  return (
    <div className="flex flex-col bg-white border-2 border-[#D3CFC0] hover:border-[#111315] transition-colors" data-testid={`tmhf-card-${p.lookup_key}`}>
      <Link to={`/shop/${p.lookup_key}`} className="block bg-[#F3F1EC] border-b-2 border-[#D3CFC0] relative">
        <img {...productImg(p.image, "(min-width: 1024px) 25vw, 50vw")} alt={p.name} loading="lazy" decoding="async" className="w-full aspect-[3/2] object-contain p-2" />
        {badge && <span className="absolute top-2 left-2 bg-[#DB9130] text-[#111315] kicker px-2 py-1" data-testid={`tmhf-badge-${p.lookup_key}`}>{badge}</span>}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <Link to={`/shop/${p.lookup_key}`} className="font-bold text-[#111315] leading-tight hover:underline underline-offset-4 flex-1">{p.name}</Link>
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="font-head text-2xl text-[#111315]">{price(p)}{p.separate_amount && <s className="ml-2 text-sm text-[#8A8D8F] font-sans">${p.separate_amount / 100}</s>}</span>
          {p.free
            ? <Link to="/cheat-sheet" className="bg-[#DB9130] text-[#111315] font-head uppercase text-xs px-3 py-2" data-testid={`tmhf-get-${p.lookup_key}`}>Get it</Link>
            : <button onClick={() => onBuy(p)} className="bg-[#DB9130] text-[#111315] font-head uppercase text-xs px-3 py-2 hover:bg-[#111315] hover:text-[#F8F7F3] transition-colors" data-testid={`tmhf-buy-${p.lookup_key}`}>Buy</button>}
        </div>
      </div>
    </div>
  );
}

export default function TeachMeHowToFootball() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get("/products").then(({ data }) => setItems((data || []).filter((p) => p.line === "tmhf"))); }, []);
  const by = (k) => items.find((p) => p.lookup_key === k);
  const list = (keys) => keys.map(by).filter(Boolean);
  const bundle = by("tmhf_bundle"), series = by("football_iq_series"), girls = by("girls_flag_bundle");
  const t1 = by("tmhf_beginners_guide");
  const scrollToForm = () => document.getElementById("cheat-sheet")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="bg-[#F5F0EA]" data-testid="tmhf-page">
      <Seo title="Teach Me How to Football" brand="VoluntoldHQ" path="/teach-me-how-to-football" image="https://voluntoldhq.com/products/tmhf_bundle.png"
        description="Football explained from zero, for parents, players and brand-new coaches." />
      <section className="bg-[#111315] text-[#F8F7F3]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <span className="kicker text-[#DB9130]">VoluntoldHQ books</span>
            <h1 className="mt-3 font-head uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.92]" data-testid="tmhf-headline">Teach Me How to Football.</h1>
            <p className="mt-5 text-lg text-[#F5F0EA]/85 max-w-xl">Football explained from zero, for parents, players and brand-new coaches.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/shop/tmhf_beginners_guide" data-testid="tmhf-hero-guide-btn"
                className="text-center bg-[#DB9130] text-[#111315] font-head uppercase tracking-wide px-6 py-4 hover:bg-[#F8F7F3] transition-colors">
                Start with the Beginner&apos;s Guide ({t1 ? price(t1) : "$14"})
              </Link>
              <button onClick={scrollToForm} data-testid="tmhf-hero-cheat-btn"
                className="border-2 border-[#F8F7F3] text-[#F8F7F3] font-head uppercase tracking-wide px-6 py-4 hover:bg-[#F8F7F3] hover:text-[#111315] transition-colors">
                Get the free Cheat Sheet
              </button>
            </div>
          </div>
          {t1 && <img {...productImg(t1.image, "(min-width: 1024px) 45vw, 100vw")} alt={t1.name} className="w-full h-auto border-2 border-[#F8F7F3]/15 shadow-[12px_12px_0_#DB9130]" data-testid="tmhf-hero-cover" />}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 space-y-16">
        <section data-testid="tmhf-who">
          <H2>Who it&apos;s for</H2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {WHO.map(([Icon, t, d]) => (
              <div key={t} className="bg-white border-2 border-[#D3CFC0] p-5">
                <Icon size={22} className="text-[#DB9130]" />
                <h3 className="mt-3 font-head uppercase text-xl text-[#111315]">{t}</h3>
                <p className="mt-2 text-sm text-[#3A3A3A]">{d}</p>
              </div>
            ))}
          </div>
        </section>

        {bundle && (
          <section data-testid="tmhf-start">
            <H2>Start here</H2>
            <p className="mt-2 text-[#3A3A3A]">The Teach Me How to Football Bundle: the Beginner&apos;s Guide, Football IQ for Players and the free Cheat Sheet.</p>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {list(bundle.bundle_of).map((p) => <BookCard key={p.lookup_key} p={p} onBuy={setActive} />)}
              <BookCard p={bundle} onBuy={setActive} badge="Bundle" />
            </div>
          </section>
        )}

        {series && (
          <section data-testid="tmhf-series">
            <H2>Football IQ Series</H2>
            <p className="mt-2 text-[#3A3A3A]">One book for every position.</p>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
              <BookCard p={series} onBuy={setActive} badge="Best value" />
              {list(series.bundle_of.filter((k) => k !== "football_iq_players")).map((p) => <BookCard key={p.lookup_key} p={p} onBuy={setActive} />)}
            </div>
          </section>
        )}

        {girls && (
          <section data-testid="tmhf-girls">
            <H2>Girls&apos; flag</H2>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
              {list(girls.bundle_of).map((p) => <BookCard key={p.lookup_key} p={p} onBuy={setActive} />)}
              <BookCard p={girls} onBuy={setActive} badge="Bundle" />
            </div>
          </section>
        )}

        <section id="cheat-sheet" className="scroll-mt-24 grid lg:grid-cols-[0.8fr_1.2fr] gap-8 items-center" data-testid="tmhf-cheat-sheet">
          <img src="/products/tmhf_sideline_cheat_sheet-800.webp" alt="The Sideline Cheat Sheet cover" loading="lazy" className="w-full h-auto border-2 border-[#D3CFC0]" />
          <div>
            <H2>Free Cheat Sheet</H2>
            <p className="mt-2 mb-6 text-[#3A3A3A]">The football words you hear on game day, explained. Free.</p>
            <div className="relative"><CheatSheetForm source="tmhf_sales_page" /></div>
          </div>
        </section>

        <section data-testid="tmhf-faq">
          <ProductFaqStatic />
        </section>
      </div>
      {active && <ShopBuyModal product={active} source="tmhf_sales_page" onClose={() => setActive(null)} />}
    </div>
  );
}

function ProductFaqStatic() {
  return <ProductFaq k="__tmhf__" extra={FAQ} refundQuestion="Refunds?" />;
}
